import React, { useEffect, useState, Children, cloneElement, isValidElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronRight, Briefcase, FileText } from 'lucide-react';
import { Modals } from './Modals';
import { ChatWidget } from './ChatWidget';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  const [modals, setModals] = useState({
    getStarted: false,
    resumeReview: false
  });
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <div className="min-h-screen bg-mesh text-slate-800 dark:text-slate-200 transition-colors duration-300 font-body flex flex-col" data-id="element-37" data-mp="zvdxh60ybjwn">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/80 dark:bg-navy-900/80 backdrop-blur-lg shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-38" data-mp="18sxx6aj61855">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-39" data-mp="2k8cujffae20s">
          <Link to="/" className="flex items-center group" aria-label="Hirelio Career Services home" data-id="element-40" data-mp="2p6xynpxnt5ui">
            <img src="https://cdn.magicpatterns.com/uploads/8YabQrVyzsQiz4gK5PGuSd/ChatGPT_Image_Jun_27,_2026,_11_11_45_AM.png" alt="Hirelio Career Services" className="h-11 w-auto object-contain dark:bg-white dark:rounded-lg dark:px-2 dark:py-1 transition-all" data-id="element-41" data-mp="1ul0mjl3xlf67" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-42" data-mp="1gtvjsa59o6rn">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`text-sm font-medium transition-colors ${location.pathname === link.path ? 'text-brand dark:text-brand-500' : 'text-slate-600 hover:text-brand dark:text-slate-300 dark:hover:text-brand-500'}`} data-id="element-43" data-mp="1bwc4d6wu1ny4">
                {link.name}
              </Link>)}
          </nav>

          <div className="hidden md:flex items-center gap-4" data-id="element-44" data-mp="t3rkug30wv6z">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-slate-500 hover:text-brand dark:text-slate-400 dark:hover:text-brand-500 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-navy-800" aria-label="Toggle Dark Mode" data-id="element-45" data-mp="34qbsyw8603us">
              {isDarkMode ? <Sun size={20} data-id="element-46" data-mp="2zsjoz3u8wra8" /> : <Moon size={20} data-id="element-47" data-mp="2zpxi76f6ift2" />}
            </button>
            <Link to="/contact" className="bg-brand hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm hover:shadow-md active:scale-95" data-id="element-48" data-mp="3niw4azrtlqau">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex md:hidden items-center gap-4" data-id="element-49" data-mp="3igyhxwo4ifdo">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-slate-500 dark:text-slate-400" data-id="element-50" data-mp="231rhuvpai9kl">
              {isDarkMode ? <Sun size={20} data-id="element-51" data-mp="2nahbdgp23u40" /> : <Moon size={20} data-id="element-52" data-mp="2nb0uh0qg0ojm" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-slate-600 dark:text-slate-300" data-id="element-53" data-mp="2ncu4mq09lqwd">
              {mobileMenuOpen ? <X size={24} data-id="element-54" data-mp="2dlh99of9cill" /> : <Menu size={24} data-id="element-55" data-mp="2div4txnnyj7l" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="fixed inset-0 z-30 bg-white dark:bg-navy-900 pt-24 px-4 pb-6 flex flex-col md:hidden" data-id="element-56" data-mp="369vbkgddq12y">
          <nav className="flex flex-col gap-4 text-lg font-medium" data-id="element-57" data-mp="31bk1c4j69ok7">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`p-3 rounded-lg ${location.pathname === link.path ? 'bg-brand-50 dark:bg-navy-800 text-brand dark:text-brand-500' : 'text-slate-700 dark:text-slate-200'}`} data-id="element-58" data-mp="2wa4ce1dvixwj">
                {link.name}
              </Link>)}
          </nav>
          <div className="mt-auto pt-6 border-t border-slate-100 dark:border-navy-800" data-id="element-59" data-mp="3siadjeq7wrqx">
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block text-center w-full bg-brand text-white py-3 rounded-xl font-medium" data-id="element-60" data-mp="e7zeabkqi2z">
              Get Started
            </Link>
          </div>
        </div>}

      {/* Main Content */}
      <main className="flex-grow pt-20" data-id="element-61" data-mp="f3xbv1p4a8ik">
        {Children.map(children, child => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            openGetStarted: () => setModals({
              ...modals,
              getStarted: true
            }),
            openResumeReview: () => setModals({
              ...modals,
              resumeReview: true
            })
          } as any);
        }
        return child;
      })}
      </main>

      {/* Footer */}
      <footer className="bg-white/50 dark:bg-navy-900/50 backdrop-blur-md border-t border-slate-200/50 dark:border-navy-800 pt-16 pb-8" data-id="element-62" data-mp="1zs3c1x6jikn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63" data-mp="3t74lok9su225">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-64" data-mp="b082x0asy5k8">
            <div data-id="element-65" data-mp="61d9z5j4f0me">
              <Link to="/" className="inline-flex items-center mb-6" aria-label="Hirelio Career Services home" data-id="element-66" data-mp="60vh8ivo34j8">
                <img src="https://cdn.magicpatterns.com/uploads/8YabQrVyzsQiz4gK5PGuSd/ChatGPT_Image_Jun_27,_2026,_11_11_45_AM.png" alt="Hirelio Career Services" className="h-12 w-auto object-contain dark:bg-white dark:rounded-lg dark:px-2 dark:py-1" data-id="element-67" data-mp="kwb4o51qs26n" />
              </Link>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed" data-id="element-68" data-mp="13q6wn8zxyqv8">
                Helping professionals get hired faster with expert resume
                writing, LinkedIn optimization, and career coaching services.
              </p>
            </div>

            <div data-id="element-69" data-mp="1nbguf4pvxdnf">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-70" data-mp="1n9w2lmuyg8t9">
                Services
              </h4>
              <ul className="space-y-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-71" data-mp="18fr6h1152qg2">
                <li data-id="element-72" data-mp="1db6kbsz5cmch">
                  <Link to="/services/resume" className="hover:text-brand transition-colors" data-id="element-73" data-mp="1dekl4ws5wnht">
                    Professional Resume
                  </Link>
                </li>
                <li data-id="element-74" data-mp="pnycb3ac2z6r">
                  <Link to="/services/executive" className="hover:text-brand transition-colors" data-id="element-75" data-mp="pohu8jzzhnks">
                    Executive Resume
                  </Link>
                </li>
                <li data-id="element-76" data-mp="gout7zb62js9">
                  <Link to="/services/linkedin" className="hover:text-brand transition-colors" data-id="element-77" data-mp="knu563vbfgt7">
                    LinkedIn Optimization
                  </Link>
                </li>
                <li data-id="element-78" data-mp="w5tbri49g76n">
                  <Link to="/services/cover-letter" className="hover:text-brand transition-colors" data-id="element-79" data-mp="w2fbqgj2ka2d">
                    Cover Letter Writing
                  </Link>
                </li>
                <li data-id="element-80" data-mp="i58x8y5doxiy">
                  <Link to="/services/coaching" className="hover:text-brand transition-colors" data-id="element-81" data-mp="i5imukmih2o4">
                    Career Coaching
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-82" data-mp="3gcpl278hmiv">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-83" data-mp="3gvxrr730wjp">
                Company
              </h4>
              <ul className="space-y-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-84" data-mp="3uo8fc7bvnc05">
                <li data-id="element-85" data-mp="3ql2bf6c8lwwu">
                  <Link to="/about" className="hover:text-brand transition-colors" data-id="element-86" data-mp="3qmn4eriwl7sl">
                    About Us
                  </Link>
                </li>
                <li data-id="element-87" data-mp="2f36f4hjctu0r">
                  <Link to="/contact" className="hover:text-brand transition-colors" data-id="element-88" data-mp="2f2wpiv281ovl">
                    Contact
                  </Link>
                </li>
                <li data-id="element-89" data-mp="1mfk5osng3v5z">
                  <a href="#" className="hover:text-brand transition-colors" data-id="element-90" data-mp="1mgbzpz1e9ku5">
                    Blog
                  </a>
                </li>
                <li data-id="element-91" data-mp="1wcyfi9ev5lpb">
                  <a href="#" className="hover:text-brand transition-colors" data-id="element-92" data-mp="1wbnfmkojgy3h">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-93" data-mp="13oj4rz2rlpjs">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-94" data-mp="13mzpaaoxnij2">
                Newsletter
              </h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-4" data-id="element-95" data-mp="18l17ktejbdth">
                Get career tips and job search strategies delivered to your
                inbox.
              </p>
              <form className="flex gap-2" onSubmit={e => e.preventDefault()} data-id="element-96" data-mp="nqvyktzg8bh4">
                <input type="email" placeholder="Email address" className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 dark:text-white" data-id="element-97" data-mp="sscvkeetbdsf" />
                <button type="submit" className="bg-brand hover:bg-brand-600 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-98" data-mp="504rjz2xox5w">
                  <ChevronRight size={20} data-id="element-99" data-mp="etlmx7ipykbo" />
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-slate-200 dark:border-navy-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-100" data-mp="3dg7yg2m82ht6">
            <p data-id="element-101" data-mp="1zl7wtxogjcqz">
              © {new Date().getFullYear()} Hirelio Career Services. All rights
              reserved.
            </p>
            <div className="flex gap-6" data-id="element-102" data-mp="1ql2q7pr38idv">
              <a href="#" className="hover:text-brand transition-colors" data-id="element-103" data-mp="1qk1cfdiothsg">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-brand transition-colors" data-id="element-104" data-mp="1locbp6uxluuk">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      <Modals getStartedOpen={modals.getStarted} resumeReviewOpen={modals.resumeReview} onClose={() => setModals({
      getStarted: false,
      resumeReview: false
    })} data-id="element-105" data-mp="1buvk89i9oru2" />

      <ChatWidget data-id="element-106" data-mp="xwdz8jjqxtfu" />
    </div>;
}